import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { Search, Eye, CheckCircle, XCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminListings() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery(['admin-listings', page, search, typeFilter, statusFilter], () => {
    const p = new URLSearchParams({ page, limit: 20 });
    if (search) p.set('search', search);
    if (typeFilter) p.set('listing_type', typeFilter);
    if (statusFilter) p.set('is_active', statusFilter);
    return api.get(`/admin/listings?${p}`).then(r => r.data);
  }, { keepPreviousData: true });

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/admin/listings/${id}`, data),
    { onSuccess: () => queryClient.invalidateQueries('admin-listings') }
  );

  const listings = data?.listings || [];
  const totalPages = data?.totalPages || 1;

  const typeLabels = { venue: 'ملعب', equipment: 'معدات', service: 'خدمة' };
  const typeColors = { venue: 'bg-green-500/20 text-green-400', equipment: 'bg-blue-500/20 text-blue-400', service: 'bg-purple-500/20 text-purple-400' };

  return (
    <div className="space-y-5" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">إدارة الإعلانات</h1>
        <span className="text-slate-400 text-sm">{data?.total || 0} إعلان</span>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-40">
          <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="form-input w-full pr-9 text-sm py-2.5" placeholder="ابحث..." />
        </div>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="form-input text-sm py-2.5">
          <option value="">جميع الأنواع</option>
          <option value="venue">ملاعب</option>
          <option value="equipment">معدات</option>
          <option value="service">خدمات</option>
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="form-input text-sm py-2.5">
          <option value="">جميع الحالات</option>
          <option value="1">نشط</option>
          <option value="0">متوقف</option>
        </select>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700/50 text-slate-400">
                <th className="text-right py-3 px-4 font-medium">الإعلان</th>
                <th className="text-right py-3 px-4 font-medium">النوع</th>
                <th className="text-right py-3 px-4 font-medium">المالك</th>
                <th className="text-right py-3 px-4 font-medium">المدينة</th>
                <th className="text-right py-3 px-4 font-medium">السعر</th>
                <th className="text-right py-3 px-4 font-medium">التقييم</th>
                <th className="text-right py-3 px-4 font-medium">مميز</th>
                <th className="text-right py-3 px-4 font-medium">الحالة</th>
                <th className="text-right py-3 px-4 font-medium">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="py-8 text-center text-slate-500">جارٍ التحميل...</td></tr>
              ) : listings.map(l => (
                <tr key={l.id} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                  <td className="py-3 px-4">
                    <p className="text-white font-medium line-clamp-1 max-w-[180px]">{l.title}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${typeColors[l.listing_type]}`}>{typeLabels[l.listing_type]}</span>
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-xs">{l.owner_name}</td>
                  <td className="py-3 px-4 text-slate-400">{l.city}</td>
                  <td className="py-3 px-4 text-primary-400">{l.price_per_hour} {l.currency}</td>
                  <td className="py-3 px-4 text-amber-400 flex items-center gap-1">
                    <Star size={11} className="fill-amber-400" />
                    {l.avg_rating ? Number(l.avg_rating).toFixed(1) : '—'}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => updateMutation.mutate({ id: l.uuid, data: { is_featured: !l.is_featured } })}
                      className={`text-xs px-2 py-0.5 rounded-full transition-colors ${l.is_featured ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700/50 text-slate-500 hover:text-amber-400'}`}
                    >
                      {l.is_featured ? 'مميز ✓' : 'تمييز'}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${l.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {l.is_active ? 'نشط' : 'متوقف'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/listings/${l.uuid}`} className="p-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white">
                        <Eye size={13} />
                      </Link>
                      <button
                        onClick={() => updateMutation.mutate({ id: l.uuid, data: { is_active: !l.is_active } })}
                        className={`p-1.5 rounded-lg transition-colors ${l.is_active ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}
                      >
                        {l.is_active ? <XCircle size={13} /> : <CheckCircle size={13} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700/50">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="btn-secondary text-sm py-1.5 disabled:opacity-40">السابق</button>
            <span className="text-slate-400 text-sm">صفحة {page} من {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="btn-secondary text-sm py-1.5 disabled:opacity-40">التالي</button>
          </div>
        )}
      </div>
    </div>
  );
}
